import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Code2, Mail, Lock, User as UserIcon, ArrowRight, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { authService } from '../utils/authService';
import { signInWithGoogle, signInWithGithub, getOAuthErrorMessage } from '../utils/firebaseAuth';
import type { AuthError } from 'firebase/auth';
import { User as AppUser } from '../types';

type AuthMode = 'login' | 'register';

interface AuthProps {
  isOpen: boolean;
  onClose: () => void;
}

// Define the User type expected by AuthContext
interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

const Auth: React.FC<AuthProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { loginWithUser } = useUser();
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Map AppUser to AuthUser format expected by AuthContext
  const mapUserToAuthUser = (user: AppUser): AuthUser => {
    return {
      id: user.id,
      name: user.username, // Map username to name
      email: user.email,
      // avatar could be added here if you have it in your user object
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      let user;
      
      if (mode === 'login') {
        // Login with real authentication service
        user = await authService.login(email, password);
        toast.success('Logged in successfully!');
      } else {
        // Register with real authentication service
        user = await authService.register(name, email, password);
        toast.success('Registered successfully!');
      }
      
      if (user) {
        // Populate the full user in UserContext (drives all pages)
        loginWithUser(user);
        // Also sync the thin AuthContext layer
        authLogin(mapUserToAuthUser(user));
        onClose();
        navigate('/profile');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(error instanceof Error ? error.message : 'Authentication failed');
      toast.error(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: 'Google' | 'GitHub') => {
    setLoading(true);
    setError(null);
    try {
      const oauthUser = provider === 'Google'
        ? await signInWithGoogle()
        : await signInWithGithub();

      if (!oauthUser.email) {
        throw new Error(
          provider === 'GitHub'
            ? 'Your GitHub email is private. Please make it public in GitHub settings, then try again.'
            : 'Could not retrieve email from provider.'
        );
      }

      // Find or create the user in our database
      const user = await authService.oauthLogin(provider.toLowerCase(), oauthUser.email, oauthUser.displayName);

      if (user) {
        loginWithUser(user);
        authLogin(mapUserToAuthUser(user));
        toast.success(`Signed in with ${provider}!`);
        onClose();
        navigate('/profile');
      } else {
        setError('Failed to sign in. Please try again.');
      }
    } catch (err: unknown) {
      const firebaseErr = err as AuthError;
      const message = firebaseErr?.code
        ? getOAuthErrorMessage(firebaseErr)
        : (err instanceof Error ? err.message : 'OAuth sign-in failed');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-cardBg rounded-xl p-8 max-w-md w-full mx-4 shadow-lg border border-accent/20 relative">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-textMuted hover:text-text transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-text mb-1">
            {mode === 'login' ? 'Welcome Back!' : 'Join TypeRush'}
          </h2>
          <p className="text-textMuted">
            {mode === 'login' 
              ? 'Log in to track your typing progress' 
              : 'Create an account to save your stats'}
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-500 rounded-lg p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {/* OAuth Buttons */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => handleOAuthLogin('Google')}
            disabled={loading}
            className="flex items-center justify-center space-x-2 bg-white text-gray-800 rounded-lg py-3 px-4 hover:bg-gray-100 transition-colors disabled:opacity-70"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
            )}
            <span>Google</span>
          </button>
          <button
            onClick={() => handleOAuthLogin('GitHub')}
            disabled={loading}
            className="flex items-center justify-center space-x-2 bg-gray-800 text-white rounded-lg py-3 px-4 hover:bg-gray-700 transition-colors disabled:opacity-70"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Code2 className="w-5 h-5" />}
            <span>GitHub</span>
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex-grow h-px bg-border/40"></div>
          <span className="px-3 text-xs text-textMuted">or continue with email</span>
          <div className="flex-grow h-px bg-border/40"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="flex items-center mb-1.5 text-sm text-textMuted">
                <UserIcon className="w-4 h-4 mr-2" />
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-secondary border border-border/20 rounded-lg px-4 py-3 text-text placeholder-textMuted focus:border-accent focus:outline-none"
                required={mode === 'register'}
                disabled={loading}
              />
            </div>
          )}
          
          <div>
            <label className="flex items-center mb-1.5 text-sm text-textMuted">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="w-full bg-secondary border border-border/20 rounded-lg px-4 py-3 text-text placeholder-textMuted focus:border-accent focus:outline-none"
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label className="flex items-center mb-1.5 text-sm text-textMuted">
              <Lock className="w-4 h-4 mr-2" />
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'register' ? 'Create a password' : 'Enter your password'}
              className="w-full bg-secondary border border-border/20 rounded-lg px-4 py-3 text-text placeholder-textMuted focus:border-accent focus:outline-none"
              required
              disabled={loading}
            />
          </div>

          {mode === 'login' && (
            <div className="text-right">
              <button 
                type="button" 
                className="text-sm text-accent hover:text-accent/80 transition-colors"
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-accent hover:bg-yellow-500 text-primary font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center disabled:opacity-70"
          >
            {loading ? (
              <span className="animate-pulse">Processing...</span>
            ) : (
              <>
                {mode === 'login' ? 'Log In' : 'Create Account'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
        </form>

        {/* Toggle mode */}
        <div className="text-center mt-6">
          <p className="text-textMuted">
            {mode === 'login' 
              ? "Don't have an account? " 
              : "Already have an account? "}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-accent hover:text-accent/80 transition-colors"
              disabled={loading}
            >
              {mode === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
